/**
 * Compact stat strip rendered on the meeting detail page.
 *
 * Surfaces the four meeting-cost facts that buyers immediately ask about:
 *   - duration
 *   - attendee count
 *   - estimated meeting cost (attendee-time, in EUR)
 *   - cost per attendee
 *
 * The salary-cost number lives in `analysis.quality.estimated_cost_eur` —
 * the LLM computes it as `attendees * 60 * duration_hours`. Showing it
 * prominently is the cheapest "this is different from Otter" demo win:
 * Otter hides cost, BYOK customers care most.
 */

import type { IStreamingSegment } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Paper, SimpleGrid, Stack, Text } from "@mantine/core"
import type { IMeetingDetail } from "../../lib/types/meeting"
import classes from "./styles.module.css"

interface IProps {
  meeting: IMeetingDetail & { attendee_emails?: string }
}

function computeDurationMinutes(meeting: IProps["meeting"]): number | null {
  const segs = meeting.streaming_segments as IStreamingSegment[] | undefined
  if (segs && segs.length > 0) {
    const first = segs[0]?.start ?? 0
    const last = segs[segs.length - 1]?.end ?? 0
    if (last > first) return Math.round((last - first) / 60)
  }
  return null
}

function countAttendees(meeting: IProps["meeting"]): number {
  if (!meeting.attendee_emails) return 0
  try {
    const parsed = JSON.parse(meeting.attendee_emails) as string[]
    return Array.isArray(parsed) ? parsed.length : 0
  } catch {
    return 0
  }
}

export function MeetingStats({ meeting }: IProps): React.JSX.Element | null {
  const { i18n } = useLingui()
  const duration = computeDurationMinutes(meeting)
  const attendeeCount = countAttendees(meeting)
  const speakerCount = meeting.speaker_names?.length ?? 0
  const cost = meeting.analysis?.quality?.estimated_cost_eur ?? null
  const costPerAttendee = cost && attendeeCount > 0 ? cost / attendeeCount : null

  // If we have nothing useful, render nothing.
  if (!duration && !attendeeCount && !speakerCount && !cost) return null

  return (
    <Paper withBorder p="md" mb="lg" className={classes.strip}>
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <Stat label={i18n._(msg`Duration`)} value={duration !== null ? i18n._(msg`${duration} min`) : "—"} />
        <Stat label={i18n._(msg`Attendees`)} value={attendeeCount > 0 ? String(attendeeCount) : "—"} />
        <Stat
          label={i18n._(msg`Speakers`)}
          value={speakerCount > 0 ? String(speakerCount) : "—"}
          hint={speakerCount > 0 ? i18n._(msg`identified by voice`) : undefined}
        />
        <Stat
          label={i18n._(msg`Cost of meeting`)}
          value={cost !== null ? `€${cost.toFixed(0)}` : "—"}
          hint={
            costPerAttendee !== null && costPerAttendee > 0
              ? i18n._(msg`≈ €${costPerAttendee.toFixed(0)} per attendee`)
              : i18n._(msg`of attendee time`)
          }
        />
      </SimpleGrid>
    </Paper>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }): React.JSX.Element {
  return (
    <Stack gap={2} className={classes.cell}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text size="xl" fw={700} className={classes.value}>
        {value}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </Stack>
  )
}
